// Zentrale, sprachbewusste Routen-Logik. Single Source of Truth ist
// src/seo/routes.json (auch vom Node-Sitemap-Generator gelesen). Hier liegen die
// typisierten Helper, mit denen App-Routing, Links, Sprachumschalter und die
// <Seo>-Komponente konsistent lokalisierte Pfade (/<lang>/<slug>) bilden.

import routesConfig from './routes.json';

export type Lang = 'de' | 'en' | 'es' | 'ru';

export interface RouteDef {
  key: string;
  changefreq: string;
  priority: number;
  slugs: Record<Lang, string>;
}

export const SITE_URL: string = routesConfig.siteUrl;
export const DEFAULT_LANG = routesConfig.defaultLang as Lang;
export const LANGUAGES = routesConfig.languages as Lang[];
export const ROUTES = routesConfig.routes as RouteDef[];

export function isLang(value: string | undefined): value is Lang {
  return !!value && (LANGUAGES as string[]).includes(value);
}

export function getRoute(key: string): RouteDef | undefined {
  return ROUTES.find((r) => r.key === key);
}

/** Slug einer Seite in einer Sprache (leerer String = Startseite). */
export function getSlug(key: string, lang: Lang): string {
  const route = getRoute(key);
  if (!route) return '';
  return route.slugs[lang] ?? route.slugs[DEFAULT_LANG] ?? '';
}

/** Lokalisierter Pfad einer Seite: "/de/implantate" bzw. "/de" für die Startseite. */
export function localizedPath(key: string, lang: Lang): string {
  const slug = getSlug(key, lang);
  return slug ? `/${lang}/${slug}` : `/${lang}`;
}

/**
 * Vollständige absolute, kanonische URL einer Seite in einer Sprache – immer MIT
 * Trailing Slash. Grund: Die Seiten werden als Verzeichnis + index.html
 * ausgeliefert (siehe scripts/prerender.js); Apache leitet die slash-lose Form
 * per 301 auf die Slash-Form um. Canonical, hreflang und Sitemap müssen daher die
 * Slash-Form verwenden, damit sie mit der final ausgelieferten URL übereinstimmen.
 * (App-internes Routing nutzt weiter localizedPath ohne Slash; React Router
 * behandelt Trailing Slashes beim Matching tolerant.)
 */
export function localizedUrl(key: string, lang: Lang): string {
  return `${SITE_URL}${localizedPath(key, lang)}/`;
}

/** Sprach-Präfix aus einem Pfad lesen (oder null, wenn keins vorhanden). */
export function langFromPath(pathname: string): Lang | null {
  const seg = pathname.replace(/^\/+/, '').split('/')[0];
  return isLang(seg) ? (seg as Lang) : null;
}

/**
 * Reverse-Lookup: Pfad → { key, lang }. Liefert null, wenn der Pfad nicht zu
 * einer bekannten lokalisierten Route gehört (z. B. Legacy-/Root-Pfade).
 */
export function findRouteByPath(pathname: string): { key: string; lang: Lang } | null {
  const segments = pathname.replace(/^\/+|\/+$/g, '').split('/');
  const maybeLang = segments[0];
  if (!isLang(maybeLang)) return null;
  const lang = maybeLang as Lang;
  const slug = segments.slice(1).join('/'); // '' für die Startseite
  const route = ROUTES.find((r) => (r.slugs[lang] ?? '') === slug);
  return route ? { key: route.key, lang } : null;
}
